import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { FiSearch, FiEye, FiCalendar, FiAward, FiTrash2 } from 'react-icons/fi'; 
import { API_BASE_URL } from '@/utils/api';

const MySwal = withReactContent(Swal);

interface AssessmentResult {
  id: number;
  user: string;
  email: string;
  date: string;
  level: string;
  score: number;
}

const Assessment: React.FC = () => {
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = `${API_BASE_URL}/admin/assessments`;

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_URL);
      const data = await res.json();
      
      if(data.success) {
        const mappedData = data.data.map((item: any) => ({
            id: item.id,
            user: item.user_name || 'Tanpa Nama',
            email: item.email || '-',
            date: new Date(item.created_at).toLocaleDateString('id-ID', { 
                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute:'2-digit' 
            }),
            level: item.level || 'BASIC',
            score: item.score
        }));
        setResults(mappedData);
      }
    } catch (err) {
      console.error("Error fetching assessments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  // --- FUNGSI HAPUS ---
  const handleDelete = async (id: number) => {
    const confirm = await MySwal.fire({
      title: 'Apakah Anda yakin?',
      text: "Data riwayat assessment ini akan dihapus permanen!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}/${id}`, {
          method: 'DELETE',
        });
        const data = await res.json();

        if (data.success) {
          MySwal.fire('Terhapus!', 'Riwayat telah berhasil dihapus.', 'success');
          // Update state lokal agar tidak perlu refresh halaman
          setResults(results.filter(item => item.id !== id));
        } else {
          MySwal.fire('Gagal', 'Gagal menghapus data dari server', 'error');
        }
      } catch (err) {
        MySwal.fire('Error', 'Terjadi kesalahan pada server', 'error');
      }
    }
  };

  const handleViewDetails = (data: AssessmentResult) => {
    MySwal.fire({
      title: `<h3 class="text-xl font-bold text-gray-800">${data.user}</h3>`,
      html: `
        <div class="flex flex-col items-center gap-4 mt-2">
           <div class="px-4 py-1 bg-red-100 text-red-700 rounded-full font-semibold text-sm">${data.level} Level</div>
           <div class="grid grid-cols-2 gap-6 w-full mt-2">
             <div class="text-center p-3 bg-gray-50 rounded-lg">
                <h1 class="text-3xl font-bold text-red-600">${data.score}</h1>
                <small class="text-gray-500 uppercase tracking-wide">Score</small>
             </div>
             <div class="text-center p-3 bg-gray-50 rounded-lg">
                <h3 class="text-md font-bold text-gray-700 mt-1">${data.date.split(',')[0]}</h3>
                <small class="text-gray-500 uppercase tracking-wide">Date</small>
             </div>
           </div>
        </div>
      `,
      confirmButtonText: 'Tutup',
      confirmButtonColor: '#dc2626',
    });
  };

  const filteredResults = results.filter(r => r.user.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h3 className="text-3xl font-extrabold text-gray-800 tracking-tight">Hasil <span className="text-red-600">Assessment</span></h3>
            <p className="text-gray-500 mt-1 text-sm">Pantau perkembangan nilai dan level siswa.</p>
          </div>
          <div className="relative w-full md:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiSearch className="text-gray-400" /></div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-red-500 transition shadow-sm"
              placeholder="Cari nama siswa..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        {loading ? (
           <div className="flex flex-col items-center justify-center py-20">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
           </div>
        ) : (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead>
                <tr className="bg-gradient-to-r from-red-600 to-red-500 text-white text-left text-sm uppercase tracking-wider font-semibold">
                  <th className="px-6 py-4">Siswa</th>
                  <th className="px-6 py-4">Tanggal Tes</th>
                  <th className="px-6 py-4 text-center">Level</th>
                  <th className="px-6 py-4 text-center">Score</th>
                  <th className="px-6 py-4 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredResults.map((item) => (
                  <tr key={item.id} className="hover:bg-red-50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold">{item.user.charAt(0).toUpperCase()}</div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-gray-900">{item.user}</div>
                          <div className="text-sm text-gray-500">{item.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-600"><FiCalendar className="mr-2 text-gray-400" />{item.date}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-200`}>
                        {item.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center"><span className="text-sm font-bold text-gray-700">{item.score}</span></td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button 
                          onClick={() => handleViewDetails(item)}
                          className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-600 hover:text-white rounded-lg transition-all"
                          title="Detail"
                        >
                          <FiEye />
                        </button>
                        <button 
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-red-600 bg-red-50 hover:bg-red-600 hover:text-white rounded-lg transition-all"
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Assessment;